import React from "react";

type FallbackProps = {
  error: Error;
  info: React.ErrorInfo;
  clearError: () => void;
};

/**
 * Minimal error boundary when Bugsnag is not configured (e.g. local dev without VITE_APP_BUGSNAG_KEY).
 */
export function createDevErrorBoundary(FallbackComponent: React.ComponentType<FallbackProps>) {
  return class DevErrorBoundary extends React.Component<
    { children: React.ReactNode },
    { error: Error | null; errorInfo: React.ErrorInfo }
  > {
    state = {
      error: null as Error | null,
      errorInfo: { componentStack: "" } as React.ErrorInfo,
    };

    static getDerivedStateFromError(error: Error) {
      return { error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
      this.setState({ error, errorInfo });
    }

    clearError = () => {
      this.setState({ error: null, errorInfo: { componentStack: "" } });
    };

    render() {
      const { error, errorInfo } = this.state;
      if (error) {
        return (
          <FallbackComponent error={error} info={errorInfo} clearError={this.clearError} />
        );
      }
      return this.props.children;
    }
  };
}
